// src/pages/CasesList/components/CreateCaseModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle, Search, User } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { createCasePhasesFromTemplates } from '../../../lib/createCasePhases';
import {
  buildDefaultCasesPrescriptionFields,
  seedDefaultCasePrescription,
} from '../../../lib/defaultCasePrescription';
import CreatePatientModal from '../../patients-list/components/CreatePatientModal';
import { patientAllowsNewCase } from '../../../lib/patientStatus';
import {
  getCaseAssistantPath,
  isAdminAssistantRole,
} from '../../../lib/dentistSubmissionWizard';

import { isActiveCase } from '../../../lib/activeCaseRules';

const LIST_MAX_HEIGHT_CLASS = 'max-h-[10.5rem]';
const LIST_MAX_HEIGHT_WIDE_CLASS = 'max-h-[9.5rem] lg:max-h-[11rem]';

function SelectionSummary({ label, title, subtitle, meta }) {
  return (
    <div
      className="mt-2 flex items-start gap-2.5 rounded-lg border border-primary/35 bg-gradient-to-r from-primary/20 via-primary/12 to-sky-500/10 px-3 py-2.5 shadow-sm ring-1 ring-inset ring-primary/15 dark:from-primary/25 dark:via-primary/15 dark:to-primary/5"
      role="status"
      aria-live="polite"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
        <Icon name="CheckCircle2" size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{label}</p>
        <p className="text-sm font-semibold text-foreground truncate">{title}</p>
        {(subtitle || meta) && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {[subtitle, meta].filter(Boolean).join(' • ')}
          </p>
        )}
      </div>
    </div>
  );
}

function PickerSearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative mb-2">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input
        type="search"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full h-9 pl-9 pr-3 text-sm border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      />
    </div>
  );
}

function PickerListItem({ selected, onSelect, primary, secondary }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full px-2.5 py-2 text-left hover:bg-muted/50 transition-colors ${
        selected ? 'bg-primary/10 border-l-4 border-primary' : 'border-l-4 border-transparent'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{primary}</p>
          <p className="text-[11px] text-muted-foreground truncate">{secondary}</p>
        </div>
        {selected && (
          <Icon name="CheckCircle2" size={18} className="text-primary flex-shrink-0" />
        )}
      </div>
    </button>
  );
}

const CreateCaseModal = ({
  isOpen,
  onClose,
  patient = null,
  currentUserId,
  currentUserRole,
  preselectedDentist = null,
  /** If set, called with created case row after success instead of default redirect. */
  onCaseCreated = null,
  /** Show « Ajout de patient » in the patient picker (e.g. hub / assistant). */
  allowAddPatient = false,
}) => {
  const navigate = useNavigate();
  const isStaffCaseCreator = isAdminAssistantRole(currentUserRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [error, setError] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(patient);
  
  // ── Admin-only: dentist selector ──────────────────────────────────────────
  const [dentists, setDentists] = useState([]);
  const [loadingDentists, setLoadingDentists] = useState(false);
  const [dentistSearchQuery, setDentistSearchQuery] = useState('');
 const [selectedDentist, setSelectedDentist] = useState(preselectedDentist);

  // ─────────────────────────────────────────────────────────────────────────
  
  const [formData, setFormData] = useState({
    title: '',
    archToTreat: '',
    treatmentType: '',
    caseType: 'aligners',
    priority: 'normal',
    questionsNotes: '',
    clinicalNotes: ''
  });

  // Charger les patients si aucun patient n'est fourni (mode création depuis la liste)
  useEffect(() => {
    if (!patient && isOpen) {
      fetchPatients();
    }
    // Admin: always fetch dentists
    if (isStaffCaseCreator && isOpen) {
      fetchDentists();
    }
  }, [patient, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedPatient(patient ?? null);
  }, [isOpen, patient]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedDentist(preselectedDentist ?? null);
  }, [isOpen, preselectedDentist]);

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);
      
      let data = [];
      let error = null;

      if (isStaffCaseCreator) {
        const result = await supabase
          .from('patients')
          .select(`
            id, 
            patient_code, 
            name, 
            email, 
            phone, 
            active_cases,
            status,
            user_profile_id,
            user_profile:user_profiles!patients_user_profile_id_fkey(
              id,
              full_name,
              email
            )
          `)
          .order('name', { ascending: true });
        
        data = result.data;
        error = result.error;
      } else if (currentUserId) {
        const result = await supabase
          .from('patients')
          .select('id, patient_code, name, email, phone, active_cases, status, user_profile_id')
          .eq('user_profile_id', currentUserId)
          .order('name', { ascending: true });
        
        data = result.data;
        error = result.error;
      } else if (!currentUserId) {
        data = [];
      }

      if (error) throw error;
      setPatients((data || []).filter((p) => patientAllowsNewCase(p)));
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError('Erreur lors du chargement des patients');
    } finally {
      setLoadingPatients(false);
    }
  };

  // ── Fetch all dentist_client users (admin only) ───────────────────────────
  const fetchDentists = async () => {
    try {
      setLoadingDentists(true);

      // 1. Get the role_id for dentist_client
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('role_type', 'dentist_client')
        .single();

      if (roleError) throw roleError;

      // 2. Get all active user_ids with that role
      const { data: assignments, error: assignError } = await supabase
        .from('user_role_assignments')
        .select('user_id')
        .eq('role_id', roleData.id)
        .eq('is_active', true);

      if (assignError) throw assignError;

      const userIds = [...new Set((assignments || []).map(a => a.user_id))];
      if (userIds.length === 0) { setDentists([]); return; }

      // 3. Fetch their profiles from user_profiles (id = auth.users id)
      const { data: profiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, phone, company_name')
        .in('id', userIds)
        .order('full_name', { ascending: true });

      if (profileError) throw profileError;

      setDentists(profiles || []);
    } catch (err) {
      console.error('Error fetching dentists:', err);
      setError('Erreur lors du chargement des dentistes');
    } finally {
      setLoadingDentists(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const filteredPatients = patients.filter((p) => {
      const query = searchQuery.toLowerCase();
      return (
        p.name?.toLowerCase().includes(query) ||
        p.patient_code?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
      );
    });

  const filteredDentists = dentists.filter(d => {
    const query = dentistSearchQuery.toLowerCase();
    return (
      d.full_name?.toLowerCase().includes(query) ||
      d.email?.toLowerCase().includes(query) ||
      d.company_name?.toLowerCase().includes(query)
    );
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      setError('Veuillez sélectionner un patient');
      return;
    }

    // Admin must also select a dentist
    if (isStaffCaseCreator && !selectedDentist) {
      setError('Veuillez sélectionner un dentiste');
      return;
    }

    if (!patientAllowsNewCase(selectedPatient)) {
      setError(
        'Ce patient est inactif. Réactivez le dossier patient pour créer une nouvelle conception.'
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // For admin: use the selected dentist's id; otherwise use current user
    const ownerProfileId =
      isStaffCaseCreator ? selectedDentist.id : currentUserId;

    try {
      const { data: livePatient, error: liveErr } = await supabase
        .from('patients')
        .select('id, status')
        .eq('id', selectedPatient.id)
        .single();

      if (liveErr) throw liveErr;
      if (!patientAllowsNewCase({ status: livePatient?.status })) {
        setError(
          'Ce patient est inactif. Réactivez le dossier patient pour créer une nouvelle conception.'
        );
        return;
      }

      // Bloquer les doublons actifs pour le même couple dentiste + patient.
      const { data: existingCases, error: existingCasesError } = await supabase
        .from('cases')
        .select('id, status, current_phase')
        .eq('patient_id', selectedPatient.id)
        .eq('user_profile_id', ownerProfileId);

      if (existingCasesError) throw existingCasesError;

      const hasActiveCase = (existingCases || []).some(isActiveCase);

      if (hasActiveCase) {
        setError(
          'Une conception est déjà en cours pour ce patient. La création est autorisée uniquement après expédition terminée (status completed et phase delivered).'
        );
        return;
      }

      const defaultPrescription = buildDefaultCasesPrescriptionFields();

      // 1. Créer le cas principal
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .insert([
          {
            patient_id: selectedPatient.id,
            user_profile_id: ownerProfileId,
            title: formData.title || `Cas ${selectedPatient.name}`,
            status: 'in_progress',
            priority: formData.priority,
            current_phase: 'submission',
            arch_to_treat: formData.archToTreat || defaultPrescription.arch_to_treat,
            treatment_type: formData.treatmentType || defaultPrescription.treatment_type,
            case_type: formData.caseType,
            questions_notes: formData.questionsNotes || null,
            clinical_notes: formData.clinicalNotes || null,
            created_date: new Date().toISOString(),
            start_date: null,
            due_date: null
          }
        ])
        .select()
        .single();

      if (caseError) throw caseError;

      await seedDefaultCasePrescription(supabase, caseData.id);

      // 2. Créer les phases + jalons depuis les templates (dynamique)
      const { error: templateError } = await createCasePhasesFromTemplates(caseData.id, ownerProfileId);
      if (templateError) throw templateError;

      // 3. Fermer tous les autres cas du patient (sauf le nouveau)
      const { error: closeError } = await supabase
        .from('cases')
        .update({ status: 'draft' })
        .eq('patient_id', selectedPatient.id)
        .neq('id', caseData.id)
        .neq('status', 'draft');

      if (closeError) throw closeError;

      // 4. Incrémenter le compteur de cas actifs du patient
      await supabase
        .from('patients')
        .update({ 
          active_cases: 1,
          last_visit: new Date().toISOString()
        })
        .eq('id', selectedPatient.id);

      // 5. Décrémenter current_case_number de l'utilisateur propriétaire
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('current_case_number')
        .eq('id', ownerProfileId)
        .single();

      if (userProfile) {
        const newCaseNumber = Math.max(0, parseInt(userProfile.current_case_number || 0) - 1);
        await supabase
          .from('user_profiles')
          .update({ current_case_number: newCaseNumber.toString() })
          .eq('id', ownerProfileId);
      }

      onClose();
      if (typeof onCaseCreated === 'function') {
        onCaseCreated(caseData);
        return;
      }
      if (isAdminAssistantRole(currentUserRole)) {
        navigate(getCaseAssistantPath(caseData.id));
        return;
      }
      window.location.href = `/case-detail/${caseData.id}?tab=files`;

    } catch (err) {
      console.error('Error creating case:', err);
      setError(err.message || 'Une erreur est survenue lors de la création du cas');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-3xl w-full max-h-[min(92vh,780px)] flex flex-col overflow-hidden border border-border/80">
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 border-b border-border">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">
              Créer une Nouvelle Commande
            </h2>
            {patient && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
                Patient : {patient.name} ({patient.patient_code})
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {allowAddPatient && !patient && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setAddPatientOpen(true)}
                iconName="UserPlus"
              >
                Ajout de patient
              </Button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4 space-y-3">
          {/* Error Alert */}
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-400/80 bg-red-50 px-3 py-2.5 flex items-start gap-2.5"
            >
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-800">Erreur</p>
                <p className="mt-0.5 text-xs sm:text-sm leading-relaxed text-red-700">{error}</p>
              </div>
            </div>
          )}

          {selectedPatient && !patientAllowsNewCase(selectedPatient) && (
            <div className="bg-muted border border-border rounded-lg px-3 py-2 text-xs sm:text-sm text-muted-foreground">
              Ce patient est inactif. La création d&apos;une nouvelle conception n&apos;est pas possible
              tant que le dossier n&apos;est pas réactivé.
            </div>
          )}

          <div
            className={
              isStaffCaseCreator && !patient
                ? 'grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4 items-start'
                : 'space-y-3'
            }
          >
          {/* ── Admin only: Sélection du dentiste ─────────────────────────── */}
          {isStaffCaseCreator && (
            <div className="min-w-0">
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Sélectionner un Dentiste <span className="text-destructive">*</span>
              </label>

              <PickerSearchInput
                value={dentistSearchQuery}
                onChange={(e) => setDentistSearchQuery(e.target.value)}
                placeholder="Rechercher un dentiste…"
              />

              <div
                className={`border border-input rounded-md overflow-y-auto ${
                  !patient ? LIST_MAX_HEIGHT_WIDE_CLASS : LIST_MAX_HEIGHT_CLASS
                }`}
              >
                {loadingDentists ? (
                  <div className="flex items-center justify-center py-6">
                    <Icon name="Loader" size={20} className="animate-spin text-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Chargement…
                    </span>
                  </div>
                ) : filteredDentists.length === 0 ? (
                  <div className="text-center py-6 px-3">
                    <Icon name="Users" size={28} className="mx-auto text-muted-foreground mb-1.5" />
                    <p className="text-sm text-muted-foreground">
                      {dentistSearchQuery ? 'Aucun dentiste trouvé' : 'Aucun dentiste disponible'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredDentists.map((d) => (
                      <PickerListItem
                        key={d.id}
                        selected={selectedDentist?.id === d.id}
                        onSelect={() => {
                          setSelectedDentist(d);
                          setError(null);
                        }}
                        primary={d.full_name}
                        secondary={[
                          d.email || "Pas d'email",
                          d.company_name,
                        ].filter(Boolean).join(' • ')}
                      />
                    ))}
                  </div>
                )}
              </div>

              {selectedDentist && (
                <SelectionSummary
                  label="Dentiste sélectionné"
                  title={selectedDentist.full_name}
                  subtitle={selectedDentist.company_name || selectedDentist.email}
                />
              )}
            </div>
          )}
          {/* ────────────────────────────────────────────────────────────────── */}

          {/* Sélection du patient (si pas déjà fourni) */}
          {!patient && (
            <div className="min-w-0">
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Sélectionner un Patient <span className="text-destructive">*</span>
              </label>

              <PickerSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un patient…"
              />

              <div
                className={`border border-input rounded-md overflow-y-auto ${
                  isStaffCaseCreator ? LIST_MAX_HEIGHT_WIDE_CLASS : LIST_MAX_HEIGHT_CLASS
                }`}
              >
                {loadingPatients ? (
                  <div className="flex items-center justify-center py-6">
                    <Icon name="Loader" size={20} className="animate-spin text-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Chargement…
                    </span>
                  </div>
                ) : filteredPatients.length === 0 ? (
                  <div className="text-center py-6 px-3">
                    <Icon name="Users" size={28} className="mx-auto text-muted-foreground mb-1.5" />
                    <p className="text-sm text-muted-foreground">
                      {searchQuery ? 'Aucun patient trouvé' : 'Aucun patient disponible'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredPatients.map((p) => (
                      <PickerListItem
                        key={p.id}
                        selected={selectedPatient?.id === p.id}
                        onSelect={() => {
                          setSelectedPatient(p);
                          setError(null);
                        }}
                        primary={p.name}
                        secondary={`${p.patient_code} • ${p.email || "Pas d'email"}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {selectedPatient && (
                <SelectionSummary
                  label="Patient sélectionné"
                  title={selectedPatient.name}
                  meta={`${selectedPatient.active_cases ?? 0} cas actif(s)`}
                />
              )}
            </div>
          )}
          </div>
          </div>

          {/* Footer : actions (fixe, sans scroll modal global) */}
          <div className="shrink-0 border-t border-border bg-card px-4 py-3 sm:px-5 sm:py-4">
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
                fullWidth
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={
                  isSubmitting ||
                  (!patient && !selectedPatient) ||
                  (isStaffCaseCreator && !selectedDentist) ||
                  !patientAllowsNewCase(selectedPatient)
                }
                fullWidth
              >
                {isSubmitting ? 'Création en cours...' : 'Créer la commande'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
    {allowAddPatient && currentUserId && (
      <CreatePatientModal
        isOpen={addPatientOpen}
        onClose={() => setAddPatientOpen(false)}
        currentUserId={currentUserId}
        zIndexClassName="z-[60]"
        onPatientCreated={async (created) => {
          setAddPatientOpen(false);
          await fetchPatients();
          setSelectedPatient({
            id: created.id,
            patient_code: created.patient_code,
            name: created.name,
            email: created.email,
            phone: created.phone,
            active_cases: created.active_cases ?? 0,
            status: created.status ?? 'active',
          });
        }}
      />
    )}
    </>
  );
};

export default CreateCaseModal;